import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Skeleton,
  IconButton,
  Chip,
  alpha,
  Divider,
  Alert,
  Button,
  Container,
  Tooltip,
  Link as MuiLink,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyIcon,
  Print as PrintIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from 'src/firebase/firebaseConfig';
import { ClientField, ClientFormInstance, FirebaseEvent } from 'shared/types/appTypes';
import { useReactToPrint } from 'react-to-print';
import { formatTimeTzNaive, formatDateValueTzNaive } from 'src/utils/formatDateTzNaive';
import { hasValue, isAnswerableField, getFormCompletionStatus } from 'src/utils/clientFormUtils';
import { useParams, useNavigate } from 'react-router';

/** Render a human-readable value for a field */
const renderFieldValue = (field: ClientField): React.ReactNode => {
  if (!('value' in field) || !hasValue(field)) {
    return null;
  }

  const v = field.value;

  switch (field.type) {
    case 'shortText':
    case 'longText':
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {String(v)}
        </Typography>
      );
    case 'number':
      return <Typography variant="body2">{v === '' ? '' : String(v)}</Typography>;
    case 'boolean':
      return (
        <Chip
          label={v === true ? 'Yes' : 'No'}
          size="small"
          color={v === true ? 'success' : 'default'}
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      );
    case 'address': {
      const address = v as {
        addressLine1?: string;
        addressLine2?: string;
        city?: string;
        state?: string;
        postalCode?: string;
        country?: string;
      };
      const line1 = (address.addressLine1 || '').trim();
      const line2 = (address.addressLine2 || '').trim();
      const cityLine = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');
      const country = (address.country || '').trim();

      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {[line1, line2, cityLine, country].filter(Boolean).join('\n')}
        </Typography>
      );
    }
    case 'date':
      return <Typography variant="body2">{formatDateValueTzNaive(v)}</Typography>;
    case 'time':
      return <Typography variant="body2">{formatTimeTzNaive(v)}</Typography>;
    case 'dateRange': {
      const range = v as { start: any; end: any };
      const start = range.start ? formatDateValueTzNaive(range.start) : '—';
      const end = range.end ? formatDateValueTzNaive(range.end) : '—';
      return (
        <Typography variant="body2">
          {start} &ndash; {end}
        </Typography>
      );
    }
    case 'timeRange': {
      const range = v as { start: any; end: any };
      const start = range.start ? formatTimeTzNaive(range.start) : '—';
      const end = range.end ? formatTimeTzNaive(range.end) : '—';
      return (
        <Typography variant="body2">
          {start} &ndash; {end}
        </Typography>
      );
    }
    case 'multipleChoice':
      return (
        <Typography variant="body2">
          {Array.isArray(v)
            ? v.filter((item) => String(item || '').trim().length > 0).join(', ')
            : String(v)}
        </Typography>
      );
    case 'imageChoice':
      return <Typography variant="body2">{String(v)}</Typography>;
    case 'appleMusicPlaylist':
      return (
        <Typography variant="body2">
          {Array.isArray(v) ? `${v.length} song${v.length === 1 ? '' : 's'} selected` : String(v)}
        </Typography>
      );
    default:
      return <Typography variant="body2">{String(v)}</Typography>;
  }
};

const ClientFormSummaryPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ClientFormInstance | null>(null);
  const [event, setEvent] = useState<FirebaseEvent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Form Summary - ${formData?.templateName || 'Responses'}`,
  });

  useEffect(() => {
    const loadData = async () => {
      if (!eventId) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);

        // Load form and event in parallel
        const [formSnap, eventSnap] = await Promise.all([
          getDoc(doc(db, 'clientForms', eventId)),
          getDoc(doc(db, 'events', eventId)),
        ]);

        if (formSnap.exists()) {
          setFormData(formSnap.data() as ClientFormInstance);
        }
        if (eventSnap.exists()) {
          setEvent({ id: eventSnap.id, ...eventSnap.data() } as FirebaseEvent);
        }
      } catch (err: any) {
        console.error('Error loading form summary:', err);
        setError(err?.message || 'Failed to load form summary');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [eventId]);

  // Filter to answerable fields only
  const answerableFields = useMemo(() => {
    return (formData?.fields || []).filter(isAnswerableField);
  }, [formData]);

  const { filledCount, totalCount } = useMemo(
    () => getFormCompletionStatus(formData),
    [formData]
  );

  // Build a map from answerable field index → original field index (for deep linking)
  const originalIndexMap = useMemo(() => {
    if (!formData?.fields) return new Map<number, number>();
    const map = new Map<number, number>();
    let answerableIdx = 0;
    formData.fields.forEach((field, originalIdx) => {
      if (isAnswerableField(field)) {
        map.set(answerableIdx, originalIdx);
        answerableIdx++;
      }
    });
    return map;
  }, [formData]);

  const handleAnswerQuestion = (answerableIdx: number) => {
    const originalIdx = originalIndexMap.get(answerableIdx) ?? 0;
    navigate('/client', {
      state: { openEventId: eventId, initialQuestionIndex: originalIdx },
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 1 }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!formData || answerableFields.length === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/client')} sx={{ mb: 2 }}>
          Back to Events
        </Button>
        <Typography color="text.secondary">No form data found for this event.</Typography>
      </Container>
    );
  }

  const eventTitle = event?.friendlyTitle || event?.dayliteData?.name || 'Event';

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/client')} sx={{ mb: 2 }}>
        Back to Events
      </Button>

      <Paper
        ref={printRef}
        variant="outlined"
        sx={{
          overflow: 'hidden',
          '@media print': {
            breakInside: 'avoid',
            boxShadow: 'none',
            border: '1px solid #ccc',
            bgcolor: '#fff !important',
            '& .MuiBox-root, & .MuiStack-root, & .MuiPaper-root': {
              bgcolor: '#fff !important',
            },
            '& .MuiTypography-root': {
              color: '#000 !important',
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {eventTitle}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formData.templateName}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                label={`${filledCount} / ${totalCount} answered`}
                size="small"
                color={filledCount === totalCount ? 'success' : 'default'}
                variant="outlined"
              />
              <Tooltip title="Print or save as PDF">
                <IconButton
                  onClick={handlePrint}
                  size="small"
                  sx={{ '@media print': { display: 'none' } }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        {/* Fields list */}
        <Box sx={{ px: 3, py: 1 }}>
          {answerableFields.map((field, idx) => {
            const filled = hasValue(field);

            return (
              <React.Fragment key={idx}>
                <Box
                  sx={{
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    ...(!filled && {
                      bgcolor: (theme) => alpha(theme.palette.warning.main, 0.04),
                      mx: -3,
                      px: 3,
                    }),
                    '@media print': {
                      pageBreakInside: 'avoid',
                      ...(!filled && {
                        bgcolor: '#f5f5f5 !important',
                      }),
                    },
                  }}
                >
                  {/* Status icon */}
                  <Box sx={{ pt: 0.25, flexShrink: 0 }}>
                    {filled ? (
                      <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    ) : (
                      <EmptyIcon sx={{ fontSize: 18, color: 'warning.main', opacity: 0.6 }} />
                    )}
                  </Box>

                  {/* Label + value */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        color: filled ? 'text.primary' : 'warning.dark',
                      }}
                    >
                      {field.label}
                    </Typography>

                    {filled && (
                      <Box sx={{ mt: 0.25 }}>{renderFieldValue(field)}</Box>
                    )}

                    {!filled && (
                      <MuiLink
                        component="button"
                        variant="caption"
                        onClick={() => handleAnswerQuestion(idx)}
                        sx={{
                          mt: 0.25,
                          color: 'warning.main',
                          cursor: 'pointer',
                          '@media print': { display: 'none' },
                        }}
                      >
                        Answer this question
                      </MuiLink>
                    )}
                  </Box>
                </Box>
                {idx < answerableFields.length - 1 && <Divider />}
              </React.Fragment>
            );
          })}
        </Box>
      </Paper>
    </Container>
  );
};

export default ClientFormSummaryPage;
